import { createSelector } from 'reselect';
import { trim } from 'lodash';
import shallowEqual from 'fbjs/lib/shallowEqual';
import { getWidgetConfig, selectMaxImageFileSize, selectMaxVideoFileSize, selectMaxFileSize } from 'root/selectors';
import joinNonEmpty from 'utils/joinNonEmpty';
import { getTabContext, selectTabContextItems, selectTabCurrentContext } from '../../common/selectors';
import { initialFilters } from '../data/ShutterStockRecord';
import consts from '../data/consts';
import buildFiltersSummary from './filtersSummaryBuilder';

const TAB = 'shutterStock';

const getContext = getTabContext(TAB);
const selectContextItems = selectTabContextItems(TAB);
const selectCurrentContext = selectTabCurrentContext(TAB);

const selectSort = (state) => state.shutterStock.sort;

const selectMaxResults = (state) => state.shutterStock.maxResults;

const selectSearchTerm = (state) => state.shutterStock.searchTerm;

const selectSearchTermText = (state) => state.shutterStock.searchTermText;

const selectMediaType = (state) => state.shutterStock.mediaType;

const selectFilters = (state) => state.shutterStock.filters;

const selectSearchParams = createSelector(
  selectSort,
  selectMaxResults,
  selectSearchTerm,
  selectMediaType,
  selectFilters,
  (sort, maxResults, searchTerm, mediaType, filters) => ({
    sort,
    maxResults,
    searchTerm,
    mediaType,
    filters,
  })
);

const selectCategories = (state) => state.shutterStock.categories;

const selectFiltersPanelOpen = (state) => state.shutterStock.filtersPanelOpen;

const selectSelectedIds = (state) => {
  const currentContext = getContext(state).context;
  return state.shutterStock.lists[currentContext].selected;
};

const selectItemsByContext = (state) => {
  const currentContext = getContext(state).context;
  return state.shutterStock.lists[currentContext].items;
};

const selectPage = (state) => {
  const currentContext = getContext(state).context;
  return state.shutterStock.lists[currentContext].page;
};

const selectAssetsPurchaseDetails = (state) => state.shutterStock.assetsPurchaseDetails;

const selectPurchaseDetails = (state) => state.shutterStock.purchase;

const selectSelectedPlan = (state) => state.shutterStock.purchase.plan;

const selectSubscriptions = (state) => state.shutterStock.account.subscriptions;

const selectSelectedItems = createSelector(selectSelectedIds, selectItemsByContext, (idCollection, items) =>
  items ? items.filter((item) => idCollection.includes(item.id)) : []
);

const selectPurchaseAssets = createSelector(selectSelectedItems, selectAssetsPurchaseDetails, (items, assetsPurchaseDetails) =>
  items
    .filter((item) => assetsPurchaseDetails[item.id])
    .map((item) => ({
      ...item,
      ...assetsPurchaseDetails[item.id],
    }))
);

const selectSubscriptionsByMediaType = createSelector(selectSubscriptions, selectMediaType, (subscriptions, mediaType) =>
  // Make sure only subscriptions which are relevant to the media type are returned.
  subscriptions.filter((subs) => subs.mediaType === mediaType)
);

const selectDefaultSubscriptionId = createSelector(selectSubscriptionsByMediaType, (subscriptions) => {
  const match = subscriptions.find((sub) => sub.valid);

  return match ? match.id : null;
});

const selectDefaultSubscriptionPrice = createSelector(selectSubscriptionsByMediaType, (subscriptions) => {
  const match = subscriptions.find((sub) => sub.valid);

  return match ? match.price_per_download?.local_amount : undefined;
});

const selectSelectedItemLicenceId = createSelector(selectSelectedItems, (selectedItems) =>
  selectedItems[0].licenceInfo ? selectedItems[0].licenceInfo.id : null
);

const selectCurrentSubscription = createSelector(
  selectSelectedPlan,
  selectSubscriptions,
  (plan, subscriptions) => plan && subscriptions.find((subs) => subs.id === plan)
);

const selectAllowedSizes = createSelector(selectCurrentSubscription, (sub) => (sub ? sub.formats.map((item) => item.size) : []));

const selectMetadataSchema = createSelector(selectCurrentSubscription, (sub) =>
  sub && sub.metadata
    ? Object.entries(sub.metadata).map(([key, val]) => ({
        key,
        required: val.is_required,
        name: val.name,
      }))
    : []
);

const selectFormat = createSelector(selectPurchaseDetails, (purchaseDetails) => consts.FORMATS_MAP[purchaseDetails.size] || '?');

const selectFileSizeLimits = createSelector(
  selectMaxImageFileSize,
  selectMaxVideoFileSize,
  selectMaxFileSize,
  (maxImageSize, maxVideoSize, maxFileSize) => ({
    maxImageSize: maxImageSize || maxFileSize,
    maxVideoSize: maxVideoSize || maxFileSize,
  })
);

const selectSizeParams = createSelector(selectAllowedSizes, selectFileSizeLimits, (allowedSizes, limits) => ({
  allowedSizes,
  ...limits,
}));

const selectAuthData = createSelector(
  getWidgetConfig,
  (state) => !!state.shutterStock.token,
  (widgetConfig, isAuth) => ({
    isAuth,
    cloudName: widgetConfig.cloudName,
  })
);

const selectIsLoggedIn = (state) => !!state.shutterStock.token;

const selectIsListeningForAuth = (state) => state.shutterStock.listeningForAuth;

const getAccount = (state) => state.shutterStock.account;

const selectUserName = createSelector(getAccount, (account) => account && account.name);

const getTrimmedSearchTerm = (params) => trim(params.searchTerm);

const selectHasSearchParams = createSelector(selectSearchParams, (params) => !!(getTrimmedSearchTerm(params) || params.filters !== initialFilters));

const selectHasSearchTerm = createSelector(selectSearchParams, (params) => !!getTrimmedSearchTerm(params));

const selectFiltersChanged = createSelector(
  selectSearchParams,
  (params) => params.filters !== initialFilters || !shallowEqual(params.filters, initialFilters)
);

const selectHasMoreAssets = createSelector(selectContextItems, ({ items, total }) => items.length < total);

const selectIsPurchaseInProgress = (state) => state.shutterStock.purchaseInProgress;

const selectShutterstockServer = createSelector(getWidgetConfig, (config) => config.shutterstockServer);

const selectPurchaseSuccessResult = (state) => ({ url: state.shutterStock.purchaseSuccessUrl });

const selectSearchSummary = createSelector(
  selectFilters,
  selectSearchTerm,
  selectMediaType,
  selectCategories,
  (filters, searchTerm, mediaType, categories) => {
    const filtersSummary = buildFiltersSummary(mediaType, filters, { categories });
    const trimmedTerm = getTrimmedSearchTerm({ searchTerm });

    return joinNonEmpty([trimmedTerm ? `[${trimmedTerm}]` : '', filtersSummary]);
  }
);

export {
  selectSort,
  selectIsLoggedIn,
  selectIsListeningForAuth,
  selectAuthData,
  selectContextItems,
  selectSearchTerm,
  selectSearchTermText,
  selectMediaType,
  selectCurrentContext,
  selectSelectedItems,
  selectSelectedItemLicenceId,
  selectPage,
  selectFiltersPanelOpen,
  selectFilters,
  selectCategories,
  selectSelectedIds,
  selectPurchaseDetails,
  selectSubscriptionsByMediaType,
  selectDefaultSubscriptionId,
  selectDefaultSubscriptionPrice,
  selectFormat,
  selectUserName,
  selectSearchParams,
  selectHasSearchParams,
  selectHasSearchTerm,
  selectHasMoreAssets,
  selectPurchaseAssets,
  selectSizeParams,
  selectCurrentSubscription,
  selectMetadataSchema,
  selectIsPurchaseInProgress,
  selectShutterstockServer,
  selectPurchaseSuccessResult,
  selectFiltersChanged,
  selectSearchSummary,
};
